import React, { useState, useRef, useCallback } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import styles from "./imagecrop.module.css";

const ImageCompressorCropper = () => {
  const [src, setSrc] = useState(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ unit: "%", width: 50, height: 50 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [compressionQuality, setCompressionQuality] = useState(0.8);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  const processFile = (file) => {
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file (JPEG, PNG, GIF, WebP)");
      return;
    }

    const reader = new FileReader();

    reader.addEventListener("load", () => {
      setSrc(reader.result);
      setProcessedImage(null);
    });

    reader.readAsDataURL(file);
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropAreaRef.current.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, []);

  const handleDropAreaClick = () => {
    fileInputRef.current?.click();
  };

  const onImageLoad = useCallback((img) => {
    setImage(img);
    const minDimension = Math.min(img.width, img.height);
    const cropSize = minDimension * 0.8;

    setCrop({
      unit: "px",
      width: cropSize,
      height: cropSize,
      x: (img.width - cropSize) / 2,
      y: (img.height - cropSize) / 2,
    });
  }, []);

  const generateProcessedImage = useCallback(async () => {
    if (!image || !completedCrop || !canvasRef.current) return;

    setIsLoading(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = completedCrop.width * pixelRatio;
    canvas.height = completedCrop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const processedUrl = URL.createObjectURL(blob);
          setProcessedImage({
            url: processedUrl,
            size: blob.size,
            width: completedCrop.width,
            height: completedCrop.height,
          });
        }
        setIsLoading(false);
      },
      "image/jpeg",
      compressionQuality
    );
  }, [image, completedCrop, compressionQuality]);

  const downloadImage = () => {
    if (processedImage && processedImage.url) {
      const link = document.createElement("a");
      link.download = `cropped-image-${Date.now()}.jpg`;
      link.href = processedImage.url;
      link.click();
    }
  };

  const resetAll = () => {
    setSrc(null);
    setImage(null);
    setProcessedImage(null);
    setCrop({ unit: "%", width: 50, height: 50 });
    setCompletedCrop(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Image Cropping Tool</h1>

      {/* Steps Guide */}
      <div className={styles.stepsGuide}>
        <h2>How to Crop Your Image</h2>
        <div className={styles.steps}>
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepContent}>
              <h4>Upload Image</h4>
              <p>Drag & drop or click to select your image file</p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepContent}>
              <h4>Select Crop Area</h4>
              <p>Drag the corners to adjust the cropping frame</p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepContent}>
              <h4>Adjust Quality</h4>
              <p>Set the compression level for your cropped image</p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>4</div>
            <div className={styles.stepContent}>
              <h4>Download Result</h4>
              <p>Save your perfectly cropped image</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      {!src && (
        <div className={styles.uploadSection}>
          <div
            ref={dropAreaRef}
            className={`${styles.dropArea} ${
              isDragOver ? styles.dragOver : ""
            }`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleDropAreaClick}
          >
            <div className={styles.dropContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <h3 className={styles.dropTitle}>Drag & Drop your image here</h3>
              <p className={styles.dropText}>or</p>
              <button className={styles.browseButton}>Browse Files</button>
              <p className={styles.supportedFormats}>
                Supports: JPEG, PNG, GIF, WebP
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              className={styles.fileInput}
            />
          </div>
        </div>
      )}

      {/* Comprehensive Content Section */}
      {!src && (
        <div className={styles.contentSection}>
          <div className={styles.contentGrid}>
            {/* Why Professional Cropping Matters */}
            <div className={styles.contentBlock}>
              <h2>The Art and Science of Professional Image Cropping</h2>
              <p>
                Image cropping is more than just cutting away unwanted parts -
                it's a powerful composition technique that can transform
                ordinary photos into compelling visual stories. Professional
                cropping enhances focus, eliminates distractions, and creates
                balanced compositions that engage viewers and convey your
                intended message effectively.
              </p>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🎯</div>
                  <h3>Enhanced Focus</h3>
                  <p>
                    Eliminate distracting elements and direct viewer attention
                    to your main subject, creating stronger visual impact and
                    clearer messaging.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>📐</div>
                  <h3>Perfect Composition</h3>
                  <p>
                    Apply professional composition rules like the Rule of
                    Thirds, Golden Ratio, and leading lines to create visually
                    balanced images.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🚀</div>
                  <h3>Optimized Performance</h3>
                  <p>
                    Remove unnecessary pixels to reduce file sizes, improve
                    loading times, and optimize images for web and mobile
                    applications.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🎨</div>
                  <h3>Creative Control</h3>
                  <p>
                    Transform image narratives by changing aspect ratios,
                    creating dynamic compositions, and emphasizing key visual
                    elements.
                  </p>
                </div>
              </div>
            </div>

            {/* Professional Cropping Techniques */}
            <div className={styles.contentBlock}>
              <h2>Advanced Cropping Techniques for Professionals</h2>
              <div className={styles.techniquesGrid}>
                <div className={styles.technique}>
                  <h4>📏 Rule of Thirds Mastery</h4>
                  <p>
                    Position key elements along the intersection points of the
                    3x3 grid to create balanced, naturally appealing
                    compositions that guide the viewer's eye through the image.
                  </p>
                  <div className={styles.techniqueTip}>
                    <strong>Pro Tip:</strong> Place eyes in the upper third for
                    portraits, horizons in the lower or upper third for
                    landscapes
                  </div>
                </div>
                <div className={styles.technique}>
                  <h4>🔍 Aspect Ratio Intelligence</h4>
                  <p>
                    Choose the perfect aspect ratio for your platform: 1:1 for
                    Instagram, 16:9 for video thumbnails, 4:5 for Facebook
                    feeds, and custom ratios for print materials.
                  </p>
                  <div className={styles.techniqueTip}>
                    <strong>Pro Tip:</strong> Maintain consistency across your
                    brand by using the same aspect ratios for similar content
                    types
                  </div>
                </div>
                <div className={styles.technique}>
                  <h4>🎭 Creative Reframing</h4>
                  <p>
                    Transform images by changing the focal point, creating
                    tension, or telling new stories through strategic cropping
                    decisions that alter the image narrative.
                  </p>
                  <div className={styles.techniqueTip}>
                    <strong>Pro Tip:</strong> Try extreme crops to create
                    abstract patterns or emphasize textures and details
                  </div>
                </div>
                <div className={styles.technique}>
                  <h4>⚡ Performance Optimization</h4>
                  <p>
                    Remove unnecessary background areas to reduce file sizes by
                    30-60% while maintaining image quality, improving website
                    performance and user experience.
                  </p>
                  <div className={styles.techniqueTip}>
                    <strong>Pro Tip:</strong> Crop to exact display dimensions
                    to eliminate unnecessary pixels and maximize loading speed
                  </div>
                </div>
              </div>
            </div>

            {/* Industry Applications */}
            <div className={styles.contentBlock}>
              <h2>Industry-Specific Cropping Applications</h2>
              <div className={styles.applicationsGrid}>
                <div className={styles.application}>
                  <h4>📱 Social Media Marketing</h4>
                  <div className={styles.applicationContent}>
                    <p>
                      <strong>Platform Requirements:</strong> Each social
                      platform has unique aspect ratio requirements that
                      dramatically affect engagement rates.
                    </p>
                    <div className={styles.platformSpecs}>
                      <div className={styles.platform}>
                        <span>Instagram Square</span>
                        <em>1:1 Ratio</em>
                      </div>
                      <div className={styles.platform}>
                        <span>Instagram Portrait</span>
                        <em>4:5 Ratio</em>
                      </div>
                      <div className={styles.platform}>
                        <span>Facebook Cover</span>
                        <em>16:9 Ratio</em>
                      </div>
                      <div className={styles.platform}>
                        <span>Twitter Header</span>
                        <em>3:1 Ratio</em>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.application}>
                  <h4>🛒 E-commerce Product Photography</h4>
                  <div className={styles.applicationContent}>
                    <p>
                      <strong>Optimization Goals:</strong> Consistent product
                      framing across catalogs, elimination of distracting
                      backgrounds, and focus on product details.
                    </p>
                    <div className={styles.ecommerceTips}>
                      <span>• Maintain consistent padding around products</span>
                      <span>• Focus on key selling features</span>
                      <span>• Create uniform thumbnail sizes</span>
                      <span>• Optimize for mobile viewing</span>
                    </div>
                  </div>
                </div>
                <div className={styles.application}>
                  <h4>📸 Professional Photography</h4>
                  <div className={styles.applicationContent}>
                    <p>
                      <strong>Creative Objectives:</strong> Enhancing
                      composition, correcting framing errors from shooting, and
                      creating multiple versions from single shots.
                    </p>
                    <div className={styles.photographyTips}>
                      <span>• Crop to eliminate distractions</span>
                      <span>• Enhance storytelling through framing</span>
                      <span>
                        • Create different aspect ratios for various uses
                      </span>
                      <span>• Maintain print resolution requirements</span>
                    </div>
                  </div>
                </div>
                <div className={styles.application}>
                  <h4>🎨 Graphic Design & Branding</h4>
                  <div className={styles.applicationContent}>
                    <p>
                      <strong>Brand Consistency:</strong> Creating perfectly
                      framed images that align with brand guidelines, design
                      systems, and visual identity standards.
                    </p>
                    <div className={styles.designTips}>
                      <span>• Maintain brand aspect ratios</span>
                      <span>• Ensure consistent image treatment</span>
                      <span>• Optimize for various media placements</span>
                      <span>• Preserve visual hierarchy</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Excellence */}
            <div className={styles.contentBlock}>
              <h2>Technical Excellence in Digital Cropping</h2>
              <div className={styles.technicalFeatures}>
                <div className={styles.technicalFeature}>
                  <h4>🖼️ High-Precision Algorithms</h4>
                  <p>
                    Our cropping tool uses advanced sub-pixel rendering
                    technology that maintains image sharpness and clarity even
                    at extreme crop ratios. The algorithms preserve edge detail
                    and prevent the blurring that often occurs with basic
                    cropping tools.
                  </p>
                </div>
                <div className={styles.technicalFeature}>
                  <h4>🎯 Smart Aspect Ratio Preservation</h4>
                  <p>
                    Intelligent constraint systems maintain your desired aspect
                    ratios while allowing flexible positioning. The tool
                    automatically suggests optimal crop areas based on image
                    content analysis and composition best practices.
                  </p>
                </div>
                <div className={styles.technicalFeature}>
                  <h4>⚡ Real-time Preview Technology</h4>
                  <p>
                    Experience instant visual feedback with our real-time
                    preview system. See exactly how your cropped image will
                    appear at different quality settings before processing,
                    eliminating guesswork and ensuring perfect results.
                  </p>
                </div>
                <div className={styles.technicalFeature}>
                  <h4>📊 Professional Quality Metrics</h4>
                  <p>
                    Access detailed information about your cropped images
                    including exact dimensions, file size reductions, quality
                    assessments, and recommendations for optimal web and print
                    usage based on your specific requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* Quality Settings Guide */}
            <div className={styles.contentBlock}>
              <h2>Mastering Compression Quality Settings</h2>
              <div className={styles.qualityGuide}>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>90-100% Quality</h4>
                    <span className={styles.qualityBadge}>
                      Print & Professional
                    </span>
                  </div>
                  <p>
                    <strong>Best for:</strong> Professional photography, print
                    materials, archival
                    <br />
                    <strong>File Size:</strong> 5-15% reduction
                    <br />
                    <strong>Visual Quality:</strong> Virtually lossless
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>70-89% Quality</h4>
                    <span className={styles.qualityBadge}>
                      Web Optimization
                    </span>
                  </div>
                  <p>
                    <strong>Best for:</strong> Websites, blogs, digital
                    publications
                    <br />
                    <strong>File Size:</strong> 40-60% reduction
                    <br />
                    <strong>Visual Quality:</strong> Excellent for web viewing
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>50-69% Quality</h4>
                    <span className={styles.qualityBadge}>Social Media</span>
                  </div>
                  <p>
                    <strong>Best for:</strong> Social media, thumbnails, email
                    <br />
                    <strong>File Size:</strong> 60-80% reduction
                    <br />
                    <strong>Visual Quality:</strong> Good for small displays
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>Below 50% Quality</h4>
                    <span className={styles.qualityBadge}>
                      Maximum Compression
                    </span>
                  </div>
                  <p>
                    <strong>Best for:</strong> Extreme size requirements only
                    <br />
                    <strong>File Size:</strong> 80-95% reduction
                    <br />
                    <strong>Visual Quality:</strong> Significant quality loss
                  </p>
                </div>
              </div>
            </div>

            {/* Workflow Integration */}
            <div className={styles.contentBlock}>
              <h2>Professional Workflow Integration</h2>
              <div className={styles.workflowSteps}>
                <div className={styles.workflowStep}>
                  <div className={styles.stepIndicator}>1</div>
                  <div className={styles.stepContent}>
                    <h4>Asset Preparation</h4>
                    <p>
                      Begin with high-resolution source images. Our tool handles
                      images up to 50MP, ensuring you have enough data for
                      creative cropping while maintaining quality.
                    </p>
                  </div>
                </div>
                <div className={styles.workflowStep}>
                  <div className={styles.stepIndicator}>2</div>
                  <div className={styles.stepContent}>
                    <h4>Strategic Cropping</h4>
                    <p>
                      Apply professional composition techniques using our
                      intuitive cropping interface with visual guides, aspect
                      ratio constraints, and real-time previews.
                    </p>
                  </div>
                </div>
                <div className={styles.workflowStep}>
                  <div className={styles.stepIndicator}>3</div>
                  <div className={styles.stepContent}>
                    <h4>Quality Optimization</h4>
                    <p>
                      Fine-tune compression settings based on your output
                      requirements, balancing file size and visual quality for
                      your specific use case.
                    </p>
                  </div>
                </div>
                <div className={styles.workflowStep}>
                  <div className={styles.stepIndicator}>4</div>
                  <div className={styles.stepContent}>
                    <h4>Final Delivery</h4>
                    <p>
                      Download perfectly cropped and optimized images ready for
                      immediate use across web, print, social media, and other
                      distribution channels.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className={styles.contentBlock}>
              <h2>Frequently Asked Questions</h2>
              <div className={styles.faqGrid}>
                <div className={styles.faqItem}>
                  <h4>Will cropping reduce my image quality?</h4>
                  <p>
                    When done correctly with our high-precision tool, cropping
                    maintains image quality by using advanced algorithms that
                    preserve sharpness and detail. The key is starting with a
                    high-resolution source image and avoiding extreme
                    enlargements of small crop areas.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>What's the difference between cropping and resizing?</h4>
                  <p>
                    Cropping removes outer parts of an image to change
                    composition or aspect ratio, while resizing changes the
                    entire image dimensions. Cropping is creative and selective,
                    while resizing affects the whole image proportionally.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>
                    Can I maintain specific aspect ratios for social media?
                  </h4>
                  <p>
                    Yes, our tool provides intuitive aspect ratio controls and
                    even includes preset ratios for major social media platforms
                    including Instagram (1:1, 4:5), Facebook (16:9), Twitter
                    (3:1), and more, ensuring your images look perfect on every
                    platform.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>How does compression affect my cropped images?</h4>
                  <p>
                    Compression reduces file size by eliminating redundant image
                    data. At higher quality settings (70-100%), the visual
                    impact is minimal. At lower settings, you'll notice quality
                    degradation, particularly in detailed areas and gradients.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>What's the maximum image size I can crop?</h4>
                  <p>
                    Our tool can handle images up to 50 megapixels
                    (approximately 8700x5800 pixels). For best results, we
                    recommend working with images between 2MP and 20MP for
                    optimal performance and quality balance.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can I undo a crop if I make a mistake?</h4>
                  <p>
                    Yes, you can easily reset the crop area at any time before
                    processing. The tool also automatically suggests a balanced
                    initial crop area to help you start with a good composition
                    foundation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editor Section */}
      {src && (
        <div className={styles.editorSection}>
          {/* Main Content Area - Side by side layout */}
          <div className={styles.mainContent}>
            {/* Left Side - Crop Editor */}
            <div className={styles.editorSide}>
              <div className={styles.cropSection}>
                <div className={styles.sectionHeader}>
                  <h3>Crop Your Image</h3>
                  <button
                    onClick={resetAll}
                    className={styles.changeImageButton}
                  >
                    Upload New Image
                  </button>
                </div>
                <p className={styles.helpText}>
                  Drag the corners or edges to adjust the crop area
                </p>
                <ReactCrop
                  crop={crop}
                  onChange={setCrop}
                  onComplete={setCompletedCrop}
                  aspect={undefined}
                  keepSelection={true}
                >
                  <img
                    ref={imageRef}
                    alt="Crop me"
                    src={src}
                    onLoad={(e) => onImageLoad(e.currentTarget)}
                    className={styles.cropImage}
                  />
                </ReactCrop>
              </div>

              {/* Controls */}
              <div className={styles.controls}>
                <div className={styles.qualityControl}>
                  <label>
                    Compression Quality: {Math.round(compressionQuality * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={compressionQuality}
                    onChange={(e) =>
                      setCompressionQuality(parseFloat(e.target.value))
                    }
                    className={styles.qualitySlider}
                  />
                </div>

                <div className={styles.buttonGroup}>
                  <button
                    onClick={generateProcessedImage}
                    disabled={isLoading || !completedCrop}
                    className={styles.processButton}
                  >
                    {isLoading ? "Processing..." : "Crop & Process Image"}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Side - Preview Section */}
            {processedImage && (
              <div className={styles.previewSide}>
                <div className={styles.previewSection}>
                  <h3>Cropped Image Ready!</h3>
                  <div className={styles.previewContent}>
                    <div className={styles.previewImageContainer}>
                      <img
                        src={processedImage.url}
                        alt="Cropped result"
                        className={styles.previewImage}
                      />
                    </div>
                    <div className={styles.imageInfo}>
                      <h4>Image Details</h4>
                      <div className={styles.detailItem}>
                        <span>File Size:</span>
                        <strong>{formatFileSize(processedImage.size)}</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Dimensions:</span>
                        <strong>
                          {processedImage.width} × {processedImage.height}px
                        </strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Quality:</span>
                        <strong>{Math.round(compressionQuality * 100)}%</strong>
                      </div>
                      <button
                        onClick={downloadImage}
                        className={styles.downloadButton}
                      >
                        Download Cropped Image
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default ImageCompressorCropper;
